// 11/02 (Josh): the popup shown whenever a Default deal is moved to another status (dropdown) or the Reverse Default
// button is clicked. Lists what GET /deals/{id}/default-reset-preview says will be voided / deleted / cleared, then the
// caller performs the status change (PATCH /deals/{id}/status) or POST /deals/{id}/reverse-default — both run the same
// backend reset (routes/deal_default.undefault_cleanup).
import React from 'react';
import { Undo2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { defaultResetHeadline, defaultResetLines } from './defaultResetModel';

export default function DefaultResetDialog({ open, onOpenChange, preview, loading, targetStatus, processing, onConfirm }) {
  const lines = defaultResetLines(preview);
  const title = targetStatus ? `Leave Default → ${targetStatus}` : 'Reverse Default Status';
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="default-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
            <Undo2 className="h-5 w-5" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {loading ? (
                <p>Checking what this deal recorded while in Default…</p>
              ) : (
                <>
                  <p className={lines.hasActivity ? 'font-medium text-red-700' : ''}>{defaultResetHeadline(preview, targetStatus)}</p>
                  {lines.voided.length > 0 && (
                    <div>
                      <p className="font-medium">Voided in the ledger (kept for audit, no longer counted):</p>
                      <ul className="list-disc list-inside">{lines.voided.map((l) => <li key={l}>{l}</li>)}</ul>
                    </div>
                  )}
                  {lines.deleted.length > 0 && (
                    <div>
                      <p className="font-medium">Removed:</p>
                      <ul className="list-disc list-inside">{lines.deleted.map((l) => <li key={l}>{l}</li>)}</ul>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">Cleared on the deal:</p>
                    <ul className="list-disc list-inside">{lines.cleared.map((l) => <li key={l}>{l}</li>)}</ul>
                  </div>
                  <p className="text-gray-600">
                    Merchant payments, bounces and bounce fees from before the default are not touched. The balance and the
                    syndication positions are recalculated right after.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={processing || loading}
            className={lines.hasActivity ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
            data-testid="default-reset-confirm"
          >
            {processing ? 'Processing...' : lines.hasActivity ? 'Remove everything and leave Default' : targetStatus ? `Switch to ${targetStatus}` : 'Reverse Default'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
